import React from "react";

// Animated waves at the bottom of a section
export const Waves = ({ className = "", color = "#00F0FF", opacity = 0.18 }) => (
  <div className={`absolute left-0 right-0 pointer-events-none ${className}`}>
    <svg viewBox="0 0 1440 200" preserveAspectRatio="none" className="w-full h-32 md:h-40">
      <defs>
        <linearGradient id="wave-grad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={opacity} />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path fill="url(#wave-grad)">
        <animate
          attributeName="d"
          dur="9s"
          repeatCount="indefinite"
          values="
            M0,100 C320,40 720,160 1440,80 L1440,200 L0,200 Z;
            M0,120 C400,180 880,40 1440,120 L1440,200 L0,200 Z;
            M0,100 C320,40 720,160 1440,80 L1440,200 L0,200 Z" />
      </path>
      <path fill={color} fillOpacity={opacity * 0.6}>
        <animate
          attributeName="d"
          dur="13s"
          repeatCount="indefinite"
          values="
            M0,140 C400,80 880,200 1440,120 L1440,200 L0,200 Z;
            M0,150 C300,210 900,90 1440,160 L1440,200 L0,200 Z;
            M0,140 C400,80 880,200 1440,120 L1440,200 L0,200 Z" />
      </path>
    </svg>
  </div>
);

// Horizontal lane-line divider (mimics pool lane rope/floats)
export const LaneLine = ({ className = "" }) => (
  <div className={`w-full flex items-center justify-center gap-1 select-none overflow-hidden ${className}`} aria-hidden>
    {Array.from({ length: 40 }).map((_, i) => (
      <span
        key={`lane-${i}`}
        className="block h-2.5 w-2.5 rounded-full"
        style={{
          background:
            i % 4 === 0
              ? "#00F0FF"
              : i % 4 === 1
              ? "#FFFFFF"
              : i % 4 === 2
              ? "#FF3B30"
              : "#FFFFFF",
          opacity: 0.85,
        }}
      />
    ))}
  </div>
);

// Vertical swim lane background (the pool floor with stripes)
export const PoolLanes = ({ className = "" }) => (
  <div className={`absolute inset-0 pointer-events-none ${className}`} aria-hidden>
    <svg className="w-full h-full" viewBox="0 0 1200 600" preserveAspectRatio="none">
      <defs>
        <linearGradient id="lane-grad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#00F0FF" stopOpacity="0.0" />
          <stop offset="50%" stopColor="#00F0FF" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#00F0FF" stopOpacity="0.0" />
        </linearGradient>
      </defs>
      {[0, 150, 300, 450, 600, 750, 900, 1050].map((x) => (
        <line key={x} x1={x} y1="0" x2={x} y2="600" stroke="url(#lane-grad)" strokeWidth="2" />
      ))}
    </svg>
  </div>
);

// Rising bubbles animation
export const Bubbles = ({ count = 14, className = "" }) => (
  <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} aria-hidden>
    {Array.from({ length: count }).map((_, i) => {
      const size = 4 + Math.random() * 10;
      const left = Math.random() * 100;
      const dur = 6 + Math.random() * 8;
      const delay = Math.random() * 6;
      return (
        <span
          key={`bubble-${i}`}
          style={{
            position: "absolute",
            left: `${left}%`,
            bottom: "-20px",
            width: `${size}px`,
            height: `${size}px`,
            borderRadius: "50%",
            border: "1px solid rgba(0,240,255,0.5)",
            background: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.6), rgba(0,240,255,0.05))",
            animation: `bubble-rise ${dur}s linear infinite`,
            animationDelay: `${delay}s`,
            opacity: 0.7,
          }}
        />
      );
    })}
  </div>
);

// Swimmer SVG silhouette
export const SwimmerIcon = ({ className = "", size = 64 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" className={className} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    {/* Stylized freestyle swimmer */}
    <circle cx="20" cy="20" r="3.5" fill="currentColor" />
    <path d="M9 28 Q14 22 22 24 Q30 26 36 22 L46 18" />
    <path d="M22 24 Q28 30 34 30 L46 30" />
    <path d="M34 30 Q40 33 48 30 L58 28" />
    <path d="M2 38 Q12 36 22 40 Q34 44 46 40 Q56 38 62 40" opacity="0.6" />
    <path d="M2 46 Q14 44 26 48 Q40 51 54 48 Q60 47 62 48" opacity="0.4" />
  </svg>
);

// Stylized water-drop / wave logo glyph (used in brand mark)
export const AamMark = ({ className = "", size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" className={className} fill="none">
    <defs>
      <linearGradient id="aam-grad" x1="0" x2="1" y1="0" y2="1">
        <stop offset="0%" stopColor="#00F0FF" />
        <stop offset="100%" stopColor="#2563EB" />
      </linearGradient>
    </defs>
    <path
      d="M32 4 C32 4 12 26 12 40 a20 20 0 0 0 40 0 C52 26 32 4 32 4 Z"
      fill="url(#aam-grad)"
    />
    <path
      d="M20 42 Q26 38 32 42 Q38 46 44 42"
      stroke="#0B1429"
      strokeWidth="2"
      strokeLinecap="round"
      fill="none"
    />
    <path
      d="M22 50 Q28 46 34 50 Q40 54 46 50"
      stroke="#0B1429"
      strokeWidth="2"
      strokeLinecap="round"
      fill="none"
      opacity="0.6"
    />
  </svg>
);

// Caustic water-light shimmer overlay
export const Caustics = ({ className = "", opacity = 0.12 }) => (
  <div
    className={`absolute inset-0 pointer-events-none mix-blend-screen ${className}`}
    style={{
      backgroundImage:
        "radial-gradient(ellipse at 20% 30%, rgba(0,240,255," + opacity + ") 0%, transparent 35%), radial-gradient(ellipse at 80% 60%, rgba(29,78,216," + opacity + ") 0%, transparent 40%), radial-gradient(ellipse at 50% 80%, rgba(0,240,255," + (opacity * 0.7) + ") 0%, transparent 35%)",
    }}
  />
);
